import { FormControl } from '@angular/forms';
import { ApDocumentType } from '../ap-document/ap-document';
import { WITHHOLDING_DOCUMENT_ENTITY_CODE } from '../withholding-document/withholding-document';

// Tax authorization (#4791) — mirrors Finance.API TaxAuthorizationDto. Formerly CaiAuthorization; the
// concept was Honduras-specific (CAI) and is now country-driven.
//
// Two kinds, told apart by their HOLDER (businessReference) rather than by entityDefinitionCode:
//  • INCOMING (provider) — held by the counterparty. Always entityDefinitionCode = ApDocument (48) plus the
//    documentType it covers: a provider's CAI only ever applies to the AP documents they issue us.
//    Recorded so their invoice numbers can be validated; managed from Business Details.
//  • SELF-ISSUED — held by the site's OWN Business, and may cover ANY of the documents we issue: ApDocument
//    (48, with a documentType discriminator — #4526) or one of the Inventory dispatch documents.
//    Managed from the Tax Authorizations page.
// Both use 48 for AP, so entityDefinitionCode alone does not separate them — the API classifies by holder.
export interface TaxAuthorization {
  id: string;
  businessReference: string;
  /** The authority's authorization code. Honduras: the CAI. */
  authorizationNumber: string;
  /** ISO-3166 alpha-3 country whose fiscal regime issued this authorization (#4791). */
  countryCode: string;
  /** Range pattern snapshot the API resolved from countryCode. Read-only — never sent by the client. */
  rangePattern: string;

  // ── Authorized band ──
  // The stored, authoritative form — and what the client supplies on write.
  /** Leader shared by every number in the band, e.g. '000-001-01-'. Stored once, so the two ends
   *  of a band can never disagree on it. */
  prefix: string;
  /** Zero-padding width of the numeric part, e.g. 8. */
  sequenceLength: number;
  /** First correlativo of the band, e.g. 2801. */
  rangeStart: number;
  /** Last correlativo of the band, e.g. 3800. */
  rangeEnd: number;

  /** Formatted band ends, RENDERED by the API from the parts above — read-only. What the dispatch PDFs
   *  print as the authorized range, and what the tables display. Never sent by the client. */
  startRange: string;
  endRange: string;
  /** Formatted next number to be issued, e.g. '000-001-01-00002803'. Null once the band is exhausted. */
  nextNumber: string | null;
  startDate: string;
  endDate: string;
  /** The next correlativo to issue, on the same numeric axis as rangeStart/rangeEnd — NOT an offset
   *  into the band. Invariant: rangeStart <= next <= rangeEnd + 1 (rangeEnd + 1 means exhausted). */
  next: number;
  active: boolean;
  entityDefinitionCode: number;
  /** Every fiscal document this authorization covers. A list because one commonly covers several — a
   *  provider's ordinary CAI covers their Invoices and Receipts, while a note gets its own. Empty on the
   *  Inventory dispatch ranges, which carry the document in their entity definition. */
  documentTypes: number[];
  /** True for system-managed authorizations (read-only in the dashboard); false for user-added ones. */
  isSystem: boolean;
}

/**
 * Payload for create/update. The band is sent DECOMPOSED — the prefix, the padding width, and the numeric
 * first/last correlativo — which is how an authorization document reads and what the operator types.
 *
 * `startRange` / `endRange` are omitted because they are rendered OUTPUT: the API formats them from the
 * parts above. Sending them too would make it possible for the formatted and decomposed forms to disagree,
 * and the formatted one is what gets printed on a fiscal document. Also omitted: `sequenceLength` and
 * `rangePattern` (properties of the COUNTRY, resolved server-side from countryCode, so a client can neither
 * pad to the wrong width nor weaken format validation), `next` (seeded from the band on create, preserved
 * on update) and `nextNumber`.
 */
export type TaxAuthorizationUpsert = Omit<
  TaxAuthorization,
  'id' | 'next' | 'rangePattern' | 'sequenceLength' | 'startRange' | 'endRange' | 'nextNumber'
> & {
  id?: string;
};

/** Strict reactive form for the add/edit authorization modal. */
export interface TaxAuthorizationForm {
  /** Which document the range covers. Fixed to ApDocument for a provider; selectable for our own. */
  entityDefinitionCode: FormControl<number | null>;
  businessReference: FormControl<string | null>;
  /** The documents an AP range covers. At least one is required; empty on the dispatch ranges. */
  documentTypes: FormControl<number[] | null>;
  authorizationNumber: FormControl<string | null>;
  /** ISO-3 country — selects the prefix shape and padding the API validates against (#4791). */
  countryCode: FormControl<string | null>;
  /** The series shared by every number in the band, e.g. '000-001-01-'. */
  prefix: FormControl<string | null>;
  /** Zero-padding width, e.g. 8. Read-only: it comes from the country, and the API resolves it again
   *  rather than trusting the value. Present so the operator can see what the numbers will look like. */
  sequenceLength: FormControl<number | null>;
  /** First correlativo as a plain number, e.g. 2801 — not the formatted '000-001-01-00002801'. */
  rangeStart: FormControl<number | null>;
  /** Last correlativo as a plain number, e.g. 3800. */
  rangeEnd: FormControl<number | null>;
  startDate: FormControl<Date | null>;
  endDate: FormControl<Date | null>;
  active: FormControl<boolean | null>;
}

/** Filters for the authorizations dashboard search bar. */
export interface TaxAuthorizationSearchForm {
  businessReference: FormControl<string | null>;
  authorizationNumber: FormControl<string | null>;
  active: FormControl<boolean | null>;
}

/** GlobalEntityDefinition.ApDocument — the entity definition for any AP fiscal range (#4526), ours or a
 *  provider's. A provider's authorization is ALWAYS this one: it covers the documents they issue us. */
export const AP_DOCUMENT_ENTITY_DEFINITION_CODE = 48;

// The Inventory dispatch documents that draw a correlativo from a self-issued range (GlobalEntityDefinition).
export const DISPATCH_INSTRUCTION_ENTITY_DEFINITION_CODE = 23;
export const SECOND_QUALITY_DISPATCH_ENTITY_DEFINITION_CODE = 37;
export const REMISSION_GUIDE_ENTITY_DEFINITION_CODE = 38;

/**
 * Documents the site issues a fiscal number for under its own name — the choices on the Tax Authorizations
 * page. Only AP Document carries a further document-type discriminator; the dispatch documents are one
 * range each. Keep in step with the draw paths: Finance.API ApDocumentService / ApRetentionService for
 * ApDocument, Inventory.API's DispatchInstruction / RemissionGuide / SecondQualityDispatch helpers.
 */
export const SELF_ISSUED_ENTITY_DEFINITIONS: { label: string; value: number }[] = [
  { label: 'AP Document', value: AP_DOCUMENT_ENTITY_DEFINITION_CODE },
  { label: 'Withholding Certificate', value: WITHHOLDING_DOCUMENT_ENTITY_CODE },
  { label: 'Dispatch Instruction', value: DISPATCH_INSTRUCTION_ENTITY_DEFINITION_CODE },
  { label: 'Remission Guide', value: REMISSION_GUIDE_ENTITY_DEFINITION_CODE },
  { label: 'Second Quality Dispatch', value: SECOND_QUALITY_DISPATCH_ENTITY_DEFINITION_CODE },
];

/** Display label for an authorization's entity definition; falls back to the raw code for anything the UI
 *  does not know about, so an unexpected row still renders instead of showing a blank cell. */
export function authorizationEntityLabel(entityDefinitionCode: number): string {
  return SELF_ISSUED_ENTITY_DEFINITIONS.find((e) => e.value === entityDefinitionCode)?.label ?? `#${entityDefinitionCode}`;
}

/**
 * The AP documents the company self-issues a correlativo for (#4526). These ranges GENERATE our numbers
 * rather than validate one a counterparty printed, which is why they are absent from the provider list.
 *
 * Debit and Credit Note were here (#4526) and are not any more (#4791): an AP note is one the provider
 * issues us, so it carries their CAI. See PROVIDER_DOCUMENT_TYPES.
 *
 * Retención was here too and is not any more (#4801): a Constancia is no longer an AP document, so its range
 * is keyed by its OWN entity definition (Withholding Certificate) with no document-type discriminator. The
 * discriminator exists to separate several ranges sharing one entity code — which is exactly what a dedicated
 * code makes unnecessary.
 */
export const SELF_ISSUED_DOCUMENT_TYPES: { label: string; value: ApDocumentType }[] = [
  { label: 'Purchase Receipt', value: ApDocumentType.PurchaseReceipt },
];

/**
 * The AP documents a PROVIDER's authorization may cover. Their ordinary CAI covers Invoice + Receipt
 * together; the authority issues a separate one for a note, which is then registered on its own.
 *
 * Purchase Receipt and Retención are absent on purpose — those are ours (see
 * {@link SELF_ISSUED_DOCUMENT_TYPES}); we generate those numbers rather than check someone else's.
 * Policy is absent too (#6016): an insurance policy is not a CAI document on either side.
 */
export const PROVIDER_DOCUMENT_TYPES: { label: string; value: ApDocumentType }[] = [
  { label: 'Invoice', value: ApDocumentType.Invoice },
  { label: 'Receipt', value: ApDocumentType.Receipt },
  { label: 'Debit Note', value: ApDocumentType.DebitNote },
  { label: 'Credit Note', value: ApDocumentType.CreditNote },
];

/** The only entity definition a provider's authorization may carry: it covers AP documents and nothing
 *  else. The dispatch ranges are ours to issue, so they never belong to a counterparty. */
export const PROVIDER_ENTITY_DEFINITIONS: { label: string; value: number }[] = [
  { label: 'AP Document', value: AP_DOCUMENT_ENTITY_DEFINITION_CODE },
];

/** AP documents WE number from our own range, so they never quote a provider CAI. Mirrors
 *  IsSelfIssuedApDocument in Finance.API. (Retención dropped off this list with #4801 — it is not an AP
 *  document at all now, so the question does not arise for it.) */
export function isSelfIssuedApDocument(documentType: ApDocumentType): boolean {
  return documentType === ApDocumentType.PurchaseReceipt;
}

/** Label for a single document-type code, across both kinds. */
export function authorizationDocumentTypeLabel(documentType: number): string {
  const known = [...SELF_ISSUED_DOCUMENT_TYPES, ...PROVIDER_DOCUMENT_TYPES].find((t) => t.value === documentType);
  return known?.label ?? `#${documentType}`;
}

/** What an authorization covers, for a table cell. Empty for the dispatch ranges, which cover a document
 *  named by their entity definition instead. */
export function authorizationCoverageLabel(documentTypes: number[] | null | undefined): string {
  if (!documentTypes?.length) return '';
  return documentTypes.map(authorizationDocumentTypeLabel).join(', ');
}

/**
 * A country's fiscal numbering format (#4791).
 *
 * A band is a prefix plus a zero-padded sequence, and both parts are country-specific: Honduras issues
 * `000-001-01-` + eight digits. The operator supplies the prefix and the numeric range; everything about
 * their SHAPE comes from here, so the form can validate as they type and show the numbers that will
 * actually be issued.
 *
 * Honduras is the only regime configured server-side today — the API rejects any other with "no fiscal
 * range format configured" — so this list stays in step with TaxAuthorizationHelper.CountryRangePatterns.
 * The API re-validates regardless: a fiscal number is a legal artefact, not a UI convenience.
 */
export interface AuthorizationCountryFormat {
  label: string;
  /** ISO-3166 alpha-3. */
  value: string;
  /** What a valid prefix looks like, including its trailing separator. */
  prefixPattern: string;
  /** Human-readable prefix shape for the field hint, e.g. '###-###-##-'. */
  prefixHint: string;
  /** Zero-padding width the regime prints. Read-only in the form and never sent — the API resolves it
   *  from the country, so it is shown for context rather than chosen. */
  sequenceLength: number;
}

/** Largest correlativo that fits the regime's printed width — 99999999 for Honduras's eight digits.
 *  Derived, not configured: a number cannot be wider than the width it is printed in. */
export function maxSequenceFor(format: AuthorizationCountryFormat | undefined): number | null {
  return format ? Math.pow(10, format.sequenceLength) - 1 : null;
}

export const AUTHORIZATION_COUNTRY_FORMATS: AuthorizationCountryFormat[] = [
  {
    label: 'Honduras (HND)',
    value: 'HND',
    prefixPattern: '^\\d{3}-\\d{3}-\\d{2}-$',
    prefixHint: '###-###-##-',
    sequenceLength: 8,
  },
];

/** Options for the country selector. */
export const AUTHORIZATION_COUNTRY_OPTIONS: { label: string; value: string }[] = AUTHORIZATION_COUNTRY_FORMATS.map((c) => ({
  label: c.label,
  value: c.value,
}));

/** Pre-selected country on a new authorization — every existing record is Honduran. */
export const DEFAULT_COUNTRY_CODE = 'HND';

export function authorizationCountryFormat(countryCode: string | null | undefined): AuthorizationCountryFormat | undefined {
  return AUTHORIZATION_COUNTRY_FORMATS.find((c) => c.value === countryCode);
}

/** Renders a correlativo the way it will be issued. Mirrors TaxAuthorizationHelper.Format server-side. */
export function formatFiscalNumber(prefix: string, sequence: number, sequenceLength: number): string {
  return `${prefix}${String(sequence).padStart(sequenceLength, '0')}`;
}
